import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { isWithinMaintenanceWindow, isBeforeMaintenanceWindow } from '../util/maintenanceTime';
import { MAINTENANCE_CONFIG, MAINTENANCE_MESSAGES } from '../constants/maintenance';

const MaintenanceContext = createContext();

/**
 * MaintenanceProvider - Provides maintenance notification state and controls
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @returns {JSX.Element} Provider component
 */
export const MaintenanceProvider = ({ children }) => {
  const [showBottomNotification, setShowBottomNotification] = useState(false);
  const [maintenanceSchedule, setMaintenanceSchedule] = useState(null);
  const [popupNotification, setPopupNotification] = useState(null);
  
  const { configs, popupNotification: storePopupNotification } = useSelector((state) => state.layout);
  
  useEffect(() => {
    if (configs?.maintenanceSchedule) {
      setMaintenanceSchedule(configs.maintenanceSchedule);
    } else {
      setMaintenanceSchedule(null);
    }
  }, [configs]);

  useEffect(() => {
    if (storePopupNotification) {
      setPopupNotification(storePopupNotification);
    } else {
      setPopupNotification(null);
    }
  }, [storePopupNotification]);

  useEffect(() => {
    if (!popupNotification) return;

    const checkMaintenanceStatus = () => {
      const { StartDate, EndDate } = popupNotification;
      
      // Only show notification BEFORE maintenance, not during
      if (isBeforeMaintenanceWindow(StartDate)) {
        setShowBottomNotification(true);
      } else {
        // Hide notification during or after maintenance
        setShowBottomNotification(false);
      }
    };

    checkMaintenanceStatus();
    const interval = setInterval(checkMaintenanceStatus, MAINTENANCE_CONFIG.CHECK_INTERVAL);

    return () => clearInterval(interval);
  }, [popupNotification]);

  const showMaintenanceBottom = useCallback(() => {
    if (!popupNotification) {
      setShowBottomNotification(true);
      return;
    }

    const { StartDate } = popupNotification;

    // Only allow manual show if maintenance is upcoming, not during
    if (isBeforeMaintenanceWindow(StartDate)) {
      setShowBottomNotification(true);
    }
  }, [popupNotification]);

  /**
   * Hide the bottom maintenance notification
   */
  const hideMaintenanceBottom = useCallback(() => {
    setShowBottomNotification(false);
  }, []);

  const value = useMemo(() => ({
    showBottomNotification,
    showMaintenanceBottom,
    hideMaintenanceBottom,
    maintenanceSchedule,
    popupNotification
  }), [showBottomNotification, showMaintenanceBottom, hideMaintenanceBottom, maintenanceSchedule, popupNotification]);
  return (
    <MaintenanceContext.Provider value={value}>
      {children}
    </MaintenanceContext.Provider>
  );
};

MaintenanceProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useMaintenanceContext = () => {
  const context = useContext(MaintenanceContext);
  if (context === undefined) {
    throw new Error(MAINTENANCE_MESSAGES.CONTEXT_ERROR);
  }
  return context;
};
